import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ExternalLinkIcon, SearchIcon, CheckCircleIcon, PhoneIcon, FileTextIcon, AlertCircleIcon, SaveIcon, ArrowRightIcon, BuildingIcon, BanIcon, UserIcon, HomeIcon, HashIcon, HelpCircleIcon, MapPinIcon, GlobeIcon } from 'lucide-react';
import { toast } from 'sonner';
// Define types locally for the component to be self-contained or import if shared
// For this implementation, I'll define the props interface based on the task description
export interface Lead {
  id: string;
  zone: string;
  rooms: number;
  price: number;
  phone: string;
  source: 'OLX' | 'Storia' | 'Imobiliare.ro';
  listingUrl: string;
  currentStep: 1 | 2 | 3;
  verificationStatus: 'pending' | 'de-prospectat' | 'exista-colaborare' | 'anunt-invalid';
  existingPropertyId?: string;
  callStatus: 'pending' | 'nu-raspunde' | 'finalizat';
  postCallStatus?: 'nu-colaboreaza' | 'comision-redus' | 'colaboreaza' | 'inchiriat-deja' | 'exclusivitate-alta' | 'de-vanzare' | 'agentie';
  propertyId?: string;
  isBlocked: boolean;
  isFinal: boolean;
  progress: '1/4' | '2/4' | '3/4' | '4/4';
  // New field for listing sources
  listingSources?: Array<{
    platform: 'OLX' | 'Storia' | 'Imobiliare.ro' | 'PubLi24' | 'Facebook' | 'Alte surse';
    url: string;
    isActive: boolean;
  }>;
}
interface LeadProcessingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
  onUpdate: (leadId: string, updates: Partial<Lead>) => void;
  onFilterChange?: (filter: 'call' | 'collab' | 'portfolio' | 'all') => void;
}
// Platform colors and icons
const platformStyles = {
  OLX: {
    bg: 'from-purple-50 to-purple-100',
    border: 'border-purple-300',
    text: 'text-purple-700',
    badge: 'bg-purple-600'
  },
  Storia: {
    bg: 'from-orange-50 to-orange-100',
    border: 'border-orange-300',
    text: 'text-orange-700',
    badge: 'bg-orange-600'
  },
  'Imobiliare.ro': {
    bg: 'from-blue-50 to-blue-100',
    border: 'border-blue-300',
    text: 'text-blue-700',
    badge: 'bg-blue-600'
  },
  PubLi24: {
    bg: 'from-green-50 to-green-100',
    border: 'border-green-300',
    text: 'text-green-700',
    badge: 'bg-green-600'
  },
  Facebook: {
    bg: 'from-indigo-50 to-indigo-100',
    border: 'border-indigo-300',
    text: 'text-indigo-700',
    badge: 'bg-indigo-600'
  },
  'Alte surse': {
    bg: 'from-slate-50 to-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-700',
    badge: 'bg-slate-600'
  }
};
// Help content for each step
const stepHelpContent = {
  step1: {
    title: 'Cum verific dacă există colaborare?',
    steps: ['Copiază numărul de telefon din header-ul lead-ului', 'Apasă butonul "Caută în CRM" pentru a verifica automat', 'Așteaptă rezultatul căutării (1-2 secunde)', 'Dacă apare "Numărul nu există" → selectează "De prospectat"', 'Dacă apare colaborare activă → selectează "Există colaborare" și introdu ID-ul', 'Dacă link-ul nu funcționează → selectează "Anunț invalid"'],
    tips: ['🔍 Verificarea în CRM este OBLIGATORIE înainte de orice apel', '🔴 Dacă există colaborare activă (status roșu) → NU contacta proprietarul', '✅ Dacă nu există → poți continua cu apelul', '⚠️ Link-urile invalide trebuie marcate imediat pentru curățare']
  },
  step2: {
    title: 'Cum efectuez apelul de prospectare?',
    steps: ['Pregătește-te: citește anunțul și notează detaliile importante', 'Sună proprietarul folosind numărul din header', 'Urmează EXACT scriptul oficial CLC (vezi lecția "Apel prospectare")', 'Dacă nu răspunde → apasă "Nu răspunde" (vei putea reîncerca mai târziu)', 'Dacă răspunde → apasă "Apel finalizat" și selectează rezultatul', 'Completează statusul corect din dropdown'],
    tips: ['📞 Sună într-un mediu liniștit, fără zgomot', '✅ Folosește scriptul oficial - nu improviza', '💬 Fii profesionist și empatic', '⏰ Cel mai bun moment: 10:00-12:00 și 17:00-19:00', '🎯 Obiectiv: obține colaborarea, nu vinde proprietatea', '📝 Notează orice detalii importante pentru pasul următor']
  },
  step3: {
    title: 'Cum finalizez colaborarea?',
    steps: ['Felicitări! Proprietarul a acceptat colaborarea', 'Accesează CRM → Secțiunea "Proprietari" → "Adaugă proprietate"', 'Completează formularul cu datele din anunț și din apel', 'Adaugă fotografii de calitate (minim 5)', 'Salvează proprietatea → CRM generează automat un ID (ex: P9211)', 'Copiază ID-ul generat', 'Revino aici și introdu ID-ul în câmpul de mai jos', 'Apasă "Finalizează Lead" pentru a închide procesul'],
    tips: ['🆔 ID-ul CRM este UNIC pentru fiecare proprietate', '📸 Fotografii de calitate = mai multe vizionări', '✅ Verifică că toate datele sunt corecte înainte de salvare', '💡 Proprietatea va fi promovată automat după indexare', '🎯 După finalizare, leadul va apărea în "Portofoliu"']
  }
};
export function LeadProcessingDrawer({
  isOpen,
  onClose,
  lead,
  onUpdate,
  onFilterChange
}: LeadProcessingDrawerProps) {
  const [localLead, setLocalLead] = useState<Lead | null>(null);
  const [existingIdInput, setExistingIdInput] = useState('');
  const [propertyIdInput, setPropertyIdInput] = useState('');
  const [verificationSelection, setVerificationSelection] = useState<string>('');
  const [postCallSelection, setPostCallSelection] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [showCallResultDropdown, setShowCallResultDropdown] = useState(false);
  const [openHelpStep, setOpenHelpStep] = useState<number | null>(null);
  useEffect(() => {
    if (lead) {
      setLocalLead(lead);
      setExistingIdInput(lead.existingPropertyId || '');
      setPropertyIdInput(lead.propertyId || '');
      setVerificationSelection(lead.verificationStatus === 'pending' ? '' : lead.verificationStatus);
      setPostCallSelection(lead.postCallStatus || '');
      setOpenHelpStep(null); // Reset help when lead changes
    }
  }, [lead]);
  if (!isOpen || !localLead) return null;
  // Mock listing sources - in production, this would come from the lead data
  const listingSources = localLead.listingSources || [{
    platform: localLead.source,
    url: localLead.listingUrl,
    isActive: true
  }];
  const handleSimulatedSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      toast.success('Căutare finalizată: Numărul nu există în baza de date.');
    }, 1000);
  };
  const handleVerificationSubmit = () => {
    if (!verificationSelection) {
      toast.error('Selectează un rezultat al verificării.');
      return;
    }
    let updates: Partial<Lead> = {
      verificationStatus: verificationSelection as any
    };
    if (verificationSelection === 'de-prospectat') {
      updates.currentStep = 2;
      updates.progress = '2/4';
      toast.success('Verificare salvată. Trecere la pasul 2.');
      onUpdate(localLead.id, updates);
      // AUTO-SWITCH to "De apelat" filter
      setTimeout(() => {
        if (onFilterChange) onFilterChange('call');
      }, 500);
    } else if (verificationSelection === 'exista-colaborare') {
      if (!existingIdInput) {
        toast.error('Introdu ID-ul proprietății existente.');
        return;
      }
      updates.existingPropertyId = existingIdInput;
      updates.isFinal = true;
      updates.isBlocked = true;
      updates.progress = '4/4';
      toast.success('Lead finalizat: Există colaborare.');
      onUpdate(localLead.id, updates);
      onClose();
    } else if (verificationSelection === 'anunt-invalid') {
      updates.isFinal = true;
      updates.isBlocked = true;
      updates.progress = '4/4';
      toast.error('Lead finalizat: Anunț invalid.');
      onUpdate(localLead.id, updates);
      onClose();
    }
  };
  const handleCallNoAnswer = () => {
    onUpdate(localLead.id, {
      callStatus: 'nu-raspunde'
      // Remains in step 2 - no step advancement
    });
    toast.info('Status setat: Nu răspunde. Leadul rămâne în lista De Apelat.');
    // Close drawer and redirect to "De Verificat" filter
    onClose();
    setTimeout(() => {
      if (onFilterChange) onFilterChange('call');
    }, 300);
  };
  const handleCallFinalized = () => {
    // Show the dropdown when "Apel finalizat" is clicked
    setShowCallResultDropdown(true);
    toast.info('Selectează rezultatul apelului din dropdown.');
  };
  const handleCallSubmit = () => {
    if (!postCallSelection) {
      toast.error('Selectează un status post-apel.');
      return;
    }
    let updates: Partial<Lead> = {
      callStatus: 'finalizat',
      postCallStatus: postCallSelection as any
    };
    if (postCallSelection === 'colaboreaza') {
      updates.currentStep = 3;
      updates.progress = '3/4';
      toast.success('Felicitări! Trecere la pasul 3: Colaborare.');
      onUpdate(localLead.id, updates);
      // AUTO-SWITCH to "Colaborări" filter
      setTimeout(() => {
        if (onFilterChange) onFilterChange('collab');
      }, 500);
    } else if (postCallSelection === 'inchiriat-deja' || postCallSelection === 'exclusivitate-alta' || postCallSelection === 'agentie') {
      updates.isBlocked = true;
      updates.isFinal = true;
      updates.progress = '4/4';
      const messages: Record<string, string> = {
        'inchiriat-deja': 'A închiriat deja',
        'exclusivitate-alta': 'Exclusivitate altă agenție',
        agentie: 'Este agenție'
      };
      toast.error(`Lead blocat: ${messages[postCallSelection]}`);
      onUpdate(localLead.id, updates);
      onClose();
      // Redirect to "Blocate" filter
      setTimeout(() => {
        if (onFilterChange) onFilterChange('all');
      }, 300);
    } else if (postCallSelection === 'de-vanzare') {
      if (confirm('Confirmi trimiterea acestui lead către departamentul de vânzări?')) {
        updates.isFinal = true;
        updates.progress = '4/4';
        toast.success('Lead trimis către departamentul de vânzări.');
        onUpdate(localLead.id, updates);
        onClose();
        // Redirect to "Toate" filter (stays only in "Toate")
        setTimeout(() => {
          if (onFilterChange) onFilterChange('all');
        }, 300);
      } else {
        return;
      }
    } else {
      // nu-colaboreaza, comision-redus - stay only in "Toate"
      updates.isFinal = true;
      updates.progress = '4/4';
      toast.success('Rezultat apel salvat.');
      onUpdate(localLead.id, updates);
      onClose();
      // Redirect to "Toate" filter (stays only in "Toate")
      setTimeout(() => {
        if (onFilterChange) onFilterChange('all');
      }, 300);
    }
  };
  const handleCollaborationSubmit = () => {
    if (!propertyIdInput) {
      toast.error('Introdu ID-ul proprietății generate.');
      return;
    }
    onUpdate(localLead.id, {
      propertyId: propertyIdInput,
      isFinal: true,
      progress: '4/4'
    });
    toast.success('Lead finalizat cu succes! Adăugat în Portofoliu.');
    // AUTO-SWITCH to "Portofoliu" filter
    setTimeout(() => {
      if (onFilterChange) onFilterChange('portfolio');
      onClose();
    }, 500);
  };
  const steps = [{
    id: 1,
    label: 'Verificare Colaborare',
    icon: SearchIcon
  }, {
    id: 2,
    label: 'Apel',
    icon: PhoneIcon
  }, {
    id: 3,
    label: 'Colaborare',
    icon: FileTextIcon
  }];
  return <AnimatePresence data-id="element-3709">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100" data-id="element-3710" />

          {/* Drawer - matching NewLeadModal style */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-110 overflow-y-auto" data-id="element-3711">
            {/* Header - REDESIGNED matching NewLeadModal */}
            <div className="bg-gradient-to-r from-[#132C53] to-[#1e4278] text-white px-6 py-5 shadow-lg" data-id="element-3712">
              <div className="flex items-center justify-between mb-4" data-id="element-3713">
                <div data-id="element-3714">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-3715">
                    <span className="px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-bold font-mono" data-id="element-3716">
                      {localLead.id}
                    </span>
                    <span className="px-3 py-1.5 rounded-lg bg-blue-500/30 backdrop-blur-sm text-white text-sm font-bold capitalize" data-id="element-3717">
                      {localLead.source}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-3718">Lead Prospectare</h3>
                  <p className="text-sm text-blue-200" data-id="element-3719">Procesare Pas cu Pas</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-3720">
                  <XIcon className="w-6 h-6 text-white" data-id="element-3721" />
                </button>
              </div>
            </div>

            {/* Property Info Card - REDESIGNED with larger, more readable text */}
            <div className="px-6 pt-4 space-y-4" data-id="element-3722">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3723">
                {/* Location Title */}
                <div className="flex items-center space-x-3 mb-4" data-id="element-3724">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center" data-id="element-3725">
                    <MapPinIcon className="w-6 h-6 text-white" data-id="element-3726" />
                  </div>
                  <div className="flex-1" data-id="element-3727">
                    <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-3728">
                      {localLead.zone}
                    </h2>
                    <a href={localLead.listingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-3729">
                      Deschide anunț original{' '}
                      <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-3730" />
                    </a>
                  </div>
                </div>

                {/* Property Details Grid - LARGER and MORE READABLE */}
                <div className="grid grid-cols-3 gap-3" data-id="element-3731">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-3732">
                    <div className="flex items-center gap-2 text-slate-500 mb-2" data-id="element-3733">
                      <HomeIcon className="w-4 h-4" data-id="element-3734" />
                      <span className="text-xs font-medium uppercase tracking-wide" data-id="element-3735">
                        Camere
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-[#132C53]" data-id="element-3736">
                      {localLead.rooms}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-3737">
                    <div className="flex items-center gap-2 text-blue-600 mb-2" data-id="element-3738">
                      <HashIcon className="w-4 h-4" data-id="element-3739" />
                      <span className="text-xs font-medium uppercase tracking-wide" data-id="element-3740">
                        Preț
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600" data-id="element-3741">
                      {localLead.price} €
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-3742">
                    <div className="flex items-center gap-2 text-emerald-600 mb-2" data-id="element-3743">
                      <PhoneIcon className="w-4 h-4" data-id="element-3744" />
                      <span className="text-xs font-medium uppercase tracking-wide" data-id="element-3745">
                        Contact
                      </span>
                    </div>
                    <p className="text-sm font-bold text-[#132C53] font-mono" data-id="element-3746">
                      {localLead.phone}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* NEW: Listing Sources Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-3747">
                <div className="flex items-center space-x-3 mb-4" data-id="element-3748">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center" data-id="element-3749">
                    <GlobeIcon className="w-5 h-5 text-white" data-id="element-3750" />
                  </div>
                  <div data-id="element-3751">
                    <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3752">
                      Surse Anunțuri
                    </h3>
                    <p className="text-xs text-slate-600" data-id="element-3753">
                      Platforme unde proprietarul și-a listat anunțul
                    </p>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-3754">
                  {listingSources.map((source, index) => {
                const style = platformStyles[source.platform];
                return <motion.div key={index} initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.2 + index * 0.05
                }} className={`bg-gradient-to-r ${style.bg} border-2 ${style.border} rounded-xl p-4 transition-all hover:shadow-md`} data-id="element-3755">
                        <div className="flex items-center justify-between" data-id="element-3756">
                          <div className="flex items-center space-x-3 flex-1" data-id="element-3757">
                            <div className={`${style.badge} w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md`} data-id="element-3758">
                              {source.platform.charAt(0)}
                            </div>
                            <div className="flex-1" data-id="element-3759">
                              <div className="flex items-center space-x-2" data-id="element-3760">
                                <p className={`font-bold ${style.text} text-sm`} data-id="element-3761">
                                  {source.platform}
                                </p>
                                {source.isActive && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold" data-id="element-3762">
                                    Activ
                                  </span>}
                              </div>
                              <a href={source.url} target="_blank" rel="noopener noreferrer" className={`text-xs ${style.text} hover:underline flex items-center space-x-1 mt-1`} data-id="element-3763">
                                <span className="truncate max-w-[200px]" data-id="element-3764">
                                  {source.url}
                                </span>
                                <ExternalLinkIcon className="w-3 h-3 flex-shrink-0" data-id="element-3765" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>;
              })}

                  {listingSources.length === 1 && <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4" data-id="element-3766">
                      <div className="flex items-start space-x-3" data-id="element-3767">
                        <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3768" />
                        <div data-id="element-3769">
                          <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-3770">
                            Verifică alte platforme
                          </p>
                          <p className="text-xs text-amber-700" data-id="element-3771">
                            Proprietarul ar putea avea anunțul și pe alte
                            platforme. Întreabă în timpul apelului!
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>
              </motion.div>
            </div>

            {/* Stepper - matching dashboard card style */}
            <div className="px-6 py-4" data-id="element-3772">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200" data-id="element-3773">
                <div className="flex justify-between relative" data-id="element-3774">
                  <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-200 -z-10 -translate-y-1/2" data-id="element-3775" />
                  {steps.map(step => {
                const isActive = localLead.currentStep === step.id;
                const isCompleted = localLead.currentStep > step.id;
                return <div key={step.id} className="flex flex-col items-center bg-white px-2" data-id="element-3776">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold mb-2 transition-all shadow-md ${isActive ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white ring-4 ring-blue-100' : isCompleted ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-3777">
                          {isCompleted ? <CheckCircleIcon className="w-5 h-5" data-id="element-3778" /> : step.id}
                        </div>
                        <span className={`text-[10px] font-medium text-center leading-tight ${isActive ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-3779">
                          {step.label}
                        </span>
                      </div>;
              })}
                </div>
              </div>
            </div>

            {/* Content - matching dashboard card style */}
            <div className="flex-1 px-6 pb-6 space-y-6" data-id="element-3780">
              {/* STEP 1: VERIFICARE COLABORARE */}
              {localLead.currentStep === 1 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="space-y-4" data-id="element-3781">
                  {/* Instructions Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3782">
                    <div className="flex items-start space-x-3 mb-4" data-id="element-3783">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-3784">
                        <SearchIcon className="w-5 h-5 text-blue-600" data-id="element-3785" />
                      </div>
                      <div className="flex-1" data-id="element-3786">
                        <h3 className="font-bold text-[#132C53] text-lg mb-1" data-id="element-3787">
                          Pasul 1: Verificare Colaborare
                        </h3>
                        <p className="text-sm text-slate-600 mb-3" data-id="element-3788">
                          Verifică dacă acest număr de telefon există deja în
                          baza de date CRM.
                        </p>

                        {/* Help Button */}
                        <button onClick={() => setOpenHelpStep(openHelpStep === 1 ? null : 1)} className="inline-flex items-center space-x-2 text-sm font-semibold text-[#357BEB] hover:text-[#2868d8] transition-colors group" data-id="element-3789">
                          <HelpCircleIcon className="w-4 h-4 group-hover:scale-110 transition-transform" data-id="element-3790" />
                          <span data-id="element-3791">Nu te descurci?</span>
                        </button>
                      </div>
                    </div>

                    {/* Help Content - Expandable */}
                    <AnimatePresence data-id="element-3792">
                      {openHelpStep === 1 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="mt-4 overflow-hidden" data-id="element-3793">
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-200" data-id="element-3794">
                            <h4 className="text-base font-bold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-3795">
                              <HelpCircleIcon className="w-5 h-5 text-[#357BEB]" data-id="element-3796" />
                              <span data-id="element-3797">{stepHelpContent.step1.title}</span>
                            </h4>

                            {/* Steps */}
                            <div className="mb-4" data-id="element-3798">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3799">
                                📋 Pași de urmat:
                              </p>
                              <ol className="space-y-2" data-id="element-3800">
                                {stepHelpContent.step1.steps.map((helpStep, idx) => <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700" data-id="element-3801">
                                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#357BEB] text-white text-xs flex items-center justify-center font-bold mt-0.5" data-id="element-3802">
                                        {idx + 1}
                                      </span>
                                      <span data-id="element-3803">{helpStep}</span>
                                    </li>)}
                              </ol>
                            </div>

                            {/* Tips */}
                            <div className="bg-white rounded-lg p-3 border border-blue-200" data-id="element-3804">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3805">
                                💡 Sfaturi importante:
                              </p>
                              <ul className="space-y-1" data-id="element-3806">
                                {stepHelpContent.step1.tips.map((tip, idx) => <li key={idx} className="text-xs text-gray-700 leading-relaxed" data-id="element-3807">
                                    {tip}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    {/* Search Input */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200" data-id="element-3808">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2" data-id="element-3809">
                        Verificare Telefon în CRM
                      </label>
                      <div className="flex gap-2" data-id="element-3810">
                        <input type="text" value={localLead.phone} readOnly className="flex-1 bg-white border-2 border-blue-200 rounded-xl px-4 py-2.5 text-slate-700 font-mono font-medium" data-id="element-3811" />
                        <button onClick={handleSimulatedSearch} disabled={isSearching} className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:shadow-lg transition-all flex items-center" data-id="element-3812">
                          {isSearching ? '...' : <SearchIcon className="w-5 h-5" data-id="element-3813" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Options Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3814">
                    <label className="block text-sm font-bold text-slate-700 mb-4" data-id="element-3815">
                      Selectează rezultatul verificării:
                    </label>

                    <div className="space-y-3" data-id="element-3816">
                      {/* De prospectat */}
                      <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${verificationSelection === 'de-prospectat' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`} data-id="element-3817">
                        <input type="radio" name="verification" value="de-prospectat" checked={verificationSelection === 'de-prospectat'} onChange={e => setVerificationSelection(e.target.value)} className="mr-3 w-5 h-5 text-blue-600 focus:ring-blue-500" data-id="element-3818" />
                        <div className="flex-1" data-id="element-3819">
                          <span className="text-slate-900 font-bold block mb-1" data-id="element-3820">
                            ✅ De prospectat
                          </span>
                          <span className="text-xs text-slate-600" data-id="element-3821">
                            Nu există colaborare în sistem. Continuă cu apelul.
                          </span>
                        </div>
                      </label>

                      {/* Există colaborare */}
                      <label className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${verificationSelection === 'exista-colaborare' ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`} data-id="element-3822">
                        <div className="flex items-center mb-2" data-id="element-3823">
                          <input type="radio" name="verification" value="exista-colaborare" checked={verificationSelection === 'exista-colaborare'} onChange={e => setVerificationSelection(e.target.value)} className="mr-3 w-5 h-5 text-orange-500 focus:ring-orange-500" data-id="element-3824" />
                          <div className="flex-1" data-id="element-3825">
                            <span className="text-slate-900 font-bold block mb-1" data-id="element-3826">
                              ⚠️ Există colaborare
                            </span>
                            <span className="text-xs text-slate-600" data-id="element-3827">
                              Proprietatea este deja în sistem. Introdu ID-ul.
                            </span>
                          </div>
                        </div>
                        {verificationSelection === 'exista-colaborare' && <input type="text" placeholder="ID Proprietate (ex: P9123)" value={existingIdInput} onChange={e => setExistingIdInput(e.target.value)} className="ml-8 w-[calc(100%-2rem)] border-2 border-orange-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none font-mono bg-white" data-id="element-3828" />}
                      </label>

                      {/* Anunț invalid */}
                      <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${verificationSelection === 'anunt-invalid' ? 'border-red-500 bg-red-50 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`} data-id="element-3829">
                        <input type="radio" name="verification" value="anunt-invalid" checked={verificationSelection === 'anunt-invalid'} onChange={e => setVerificationSelection(e.target.value)} className="mr-3 w-5 h-5 text-red-500 focus:ring-red-500" data-id="element-3830" />
                        <div className="flex-1" data-id="element-3831">
                          <span className="text-slate-900 font-bold block mb-1" data-id="element-3832">
                            ❌ Anunț Invalid
                          </span>
                          <span className="text-xs text-slate-600" data-id="element-3833">
                            Link-ul nu funcționează sau anunțul a fost șters.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                </motion.div>}

              {/* STEP 2: APEL */}
              {localLead.currentStep === 2 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="space-y-4" data-id="element-3834">
                  {/* Instructions Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3835">
                    <div className="flex items-start space-x-3" data-id="element-3836">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-3837">
                        <PhoneIcon className="w-5 h-5 text-orange-600" data-id="element-3838" />
                      </div>
                      <div className="flex-1" data-id="element-3839">
                        <h3 className="font-bold text-[#132C53] text-lg mb-1" data-id="element-3840">
                          Pasul 2: Apel Telefonic
                        </h3>
                        <p className="text-sm text-slate-600 mb-3" data-id="element-3841">
                          Sună proprietarul și înregistrează rezultatul
                          apelului.
                        </p>

                        {/* Help Button */}
                        <button onClick={() => setOpenHelpStep(openHelpStep === 2 ? null : 2)} className="inline-flex items-center space-x-2 text-sm font-semibold text-[#357BEB] hover:text-[#2868d8] transition-colors group" data-id="element-3842">
                          <HelpCircleIcon className="w-4 h-4 group-hover:scale-110 transition-transform" data-id="element-3843" />
                          <span data-id="element-3844">Nu te descurci?</span>
                        </button>
                      </div>
                    </div>

                    {/* Help Content - Expandable */}
                    <AnimatePresence data-id="element-3845">
                      {openHelpStep === 2 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="mt-4 overflow-hidden" data-id="element-3846">
                          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border-2 border-orange-200" data-id="element-3847">
                            <h4 className="text-base font-bold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-3848">
                              <HelpCircleIcon className="w-5 h-5 text-orange-600" data-id="element-3849" />
                              <span data-id="element-3850">{stepHelpContent.step2.title}</span>
                            </h4>

                            {/* Steps */}
                            <div className="mb-4" data-id="element-3851">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3852">
                                📋 Pași de urmat:
                              </p>
                              <ol className="space-y-2" data-id="element-3853">
                                {stepHelpContent.step2.steps.map((helpStep, idx) => <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700" data-id="element-3854">
                                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-600 text-white text-xs flex items-center justify-center font-bold mt-0.5" data-id="element-3855">
                                        {idx + 1}
                                      </span>
                                      <span data-id="element-3856">{helpStep}</span>
                                    </li>)}
                              </ol>
                            </div>

                            {/* Tips */}
                            <div className="bg-white rounded-lg p-3 border border-orange-200" data-id="element-3857">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3858">
                                💡 Sfaturi importante:
                              </p>
                              <ul className="space-y-1" data-id="element-3859">
                                {stepHelpContent.step2.tips.map((tip, idx) => <li key={idx} className="text-xs text-gray-700 leading-relaxed" data-id="element-3860">
                                    {tip}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Action Buttons Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3861">
                    <div className="grid grid-cols-2 gap-4" data-id="element-3862">
                      <button onClick={handleCallNoAnswer} className="flex flex-col items-center justify-center p-6 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-lg transition-all group" data-id="element-3863">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-orange-500 mb-3 shadow-md group-hover:scale-110 transition-transform" data-id="element-3864">
                          <PhoneIcon className="w-6 h-6" data-id="element-3865" />
                        </div>
                        <span className="font-bold text-orange-700 mb-1" data-id="element-3866">
                          Nu răspunde
                        </span>
                        <span className="text-xs text-orange-600 text-center" data-id="element-3867">
                          Vei putea reîncerca
                        </span>
                      </button>

                      <button onClick={handleCallFinalized} className="flex flex-col items-center justify-center p-6 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl hover:shadow-lg transition-all group" data-id="element-3868">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 mb-3 shadow-md group-hover:scale-110 transition-transform" data-id="element-3869">
                          <CheckCircleIcon className="w-6 h-6" data-id="element-3870" />
                        </div>
                        <span className="font-bold text-[#132C53] mb-1" data-id="element-3871">
                          Apel Finalizat
                        </span>
                        <span className="text-xs text-blue-600 text-center" data-id="element-3872">
                          Ai vorbit cu proprietarul
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Dropdown Card */}
                  {showCallResultDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3873">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-r-xl mb-4" data-id="element-3874">
                        <p className="text-sm font-bold text-[#132C53]" data-id="element-3875">
                          📋 Selectează rezultatul conversației:
                        </p>
                      </div>
                      <select value={postCallSelection} onChange={e => setPostCallSelection(e.target.value)} className="w-full border-2 border-slate-300 rounded-xl px-4 py-3 text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white font-medium shadow-sm" data-id="element-3876">
                        <option value="" data-id="element-3877">Selectează rezultatul...</option>
                        <option value="colaboreaza" data-id="element-3878">✅ Colaborează</option>
                        <option value="nu-colaboreaza" data-id="element-3879">
                          ❌ Nu colaborează
                        </option>
                        <option value="comision-redus" data-id="element-3880">
                          📉 Oferă comision redus
                        </option>
                        <option value="inchiriat-deja" data-id="element-3881">
                          🏠 A închiriat deja
                        </option>
                        <option value="exclusivitate-alta" data-id="element-3882">
                          🔒 Exclusivitate altă agenție
                        </option>
                        <option value="de-vanzare" data-id="element-3883">💰 De vânzare</option>
                        <option value="agentie" data-id="element-3884">🏢 Agenție</option>
                      </select>

                      {postCallSelection === 'de-vanzare' && <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-xl border-l-4 border-yellow-400 flex items-start gap-3 text-sm text-yellow-900 mt-4" data-id="element-3885">
                          <AlertCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" data-id="element-3886" />
                          <div data-id="element-3887">
                            <p className="font-bold mb-1" data-id="element-3888">
                              Proprietate de vânzare
                            </p>
                            <p data-id="element-3889">
                              Acest lead va fi trimis către departamentul de
                              vânzări.
                            </p>
                          </div>
                        </div>}
                    </motion.div>}
                </motion.div>}

              {/* STEP 3: COLABORARE */}
              {localLead.currentStep === 3 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="space-y-4" data-id="element-3890">
                  {/* Instructions Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3891">
                    <div className="flex items-start space-x-3 mb-4" data-id="element-3892">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-3893">
                        <BuildingIcon className="w-5 h-5 text-emerald-600" data-id="element-3894" />
                      </div>
                      <div className="flex-1" data-id="element-3895">
                        <h3 className="font-bold text-[#132C53] text-lg mb-1" data-id="element-3896">
                          Pasul 3: Finalizare Colaborare
                        </h3>
                        <p className="text-sm text-slate-600 mb-3" data-id="element-3897">
                          Colaborarea a fost acceptată! Creează anunțul în
                          sistem.
                        </p>

                        {/* Help Button */}
                        <button onClick={() => setOpenHelpStep(openHelpStep === 3 ? null : 3)} className="inline-flex items-center space-x-2 text-sm font-semibold text-[#357BEB] hover:text-[#2868d8] transition-colors group" data-id="element-3898">
                          <HelpCircleIcon className="w-4 h-4 group-hover:scale-110 transition-transform" data-id="element-3899" />
                          <span data-id="element-3900">Nu te descurci?</span>
                        </button>
                      </div>
                    </div>

                    {/* Help Content - Expandable */}
                    <AnimatePresence data-id="element-3901">
                      {openHelpStep === 3 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="mt-4 overflow-hidden" data-id="element-3902">
                          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 border-2 border-emerald-200" data-id="element-3903">
                            <h4 className="text-base font-bold text-[#132C53] mb-3 flex items-center space-x-2" data-id="element-3904">
                              <HelpCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-3905" />
                              <span data-id="element-3906">{stepHelpContent.step3.title}</span>
                            </h4>

                            {/* Steps */}
                            <div className="mb-4" data-id="element-3907">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3908">
                                📋 Pași de urmat:
                              </p>
                              <ol className="space-y-2" data-id="element-3909">
                                {stepHelpContent.step3.steps.map((helpStep, idx) => <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700" data-id="element-3910">
                                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center font-bold mt-0.5" data-id="element-3911">
                                        {idx + 1}
                                      </span>
                                      <span data-id="element-3912">{helpStep}</span>
                                    </li>)}
                              </ol>
                            </div>

                            {/* Tips */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-200" data-id="element-3913">
                              <p className="text-xs font-semibold text-[#132C53] mb-2" data-id="element-3914">
                                💡 Sfaturi importante:
                              </p>
                              <ul className="space-y-1" data-id="element-3915">
                                {stepHelpContent.step3.tips.map((tip, idx) => <li key={idx} className="text-xs text-gray-700 leading-relaxed" data-id="element-3916">
                                    {tip}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-xl border-2 border-emerald-200 text-center" data-id="element-3917">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-emerald-600 mx-auto mb-4 shadow-lg" data-id="element-3918">
                        <CheckCircleIcon className="w-8 h-8" data-id="element-3919" />
                      </div>
                      <h3 className="font-bold text-emerald-800 text-lg mb-2" data-id="element-3920">
                        🎉 Felicitări! Colaborare acceptată!
                      </h3>
                      <p className="text-sm text-emerald-700" data-id="element-3921">
                        Pentru a finaliza, creează anunțul și introdu ID-ul
                        proprietății generat.
                      </p>
                    </div>
                  </div>

                  {/* Input Card */}
                  <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-3922">
                    <label className="block text-sm font-bold text-slate-700 mb-3" data-id="element-3923">
                      ID Proprietate Generat
                    </label>
                    <div className="flex gap-2" data-id="element-3924">
                      <input type="text" placeholder="ex: P9200" value={propertyIdInput} onChange={e => setPropertyIdInput(e.target.value)} className="flex-1 border-2 border-emerald-300 rounded-xl px-4 py-3 text-slate-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-mono font-bold bg-gradient-to-br from-emerald-50 to-green-50" data-id="element-3925" />
                      <button className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-colors text-sm border-2 border-slate-200" data-id="element-3926">
                        Creează Anunț
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-2" data-id="element-3927">
                      <AlertCircleIcon className="w-3 h-3" data-id="element-3928" />
                      După ce creezi anunțul, copiază ID-ul și introdu-l mai
                      sus.
                    </p>
                  </div>
                </motion.div>}
            </div>

            {/* Footer Actions - matching dashboard style */}
            <div className="p-6 border-t border-slate-200 bg-white" data-id="element-3929">
              {localLead.currentStep === 1 && <button onClick={handleVerificationSubmit} className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2" data-id="element-3930">
                  Confirmă Verificarea <ArrowRightIcon className="w-4 h-4" data-id="element-3931" />
                </button>}

              {localLead.currentStep === 2 && <button onClick={handleCallSubmit} className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2" data-id="element-3932">
                  <SaveIcon className="w-4 h-4" data-id="element-3933" /> Salvează Rezultat Apel
                </button>}

              {localLead.currentStep === 3 && <button onClick={handleCollaborationSubmit} className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2" data-id="element-3934">
                  <CheckCircleIcon className="w-4 h-4" data-id="element-3935" /> Finalizează Lead
                </button>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}